/*
 * FarmSteady — interaction event tracking (2026-08-02 batch).
 *
 * Pushes fs_* events into window.dataLayer for GTM to forward to GA4.
 * One capture-phase delegated click listener; selectors are the stable
 * class hooks each section already renders. Pushes are inert until the
 * GTM container has a matching Custom Event trigger (regex ^fs_), so
 * shipping this ahead of the GTM config is safe.
 *
 * Event vocabulary (event name = the decision surface, fs_label = which
 * specific thing, fs_area = sub-slot where one surface has several):
 *   fs_drawer_open / fs_drawer_close  — nav drawer toggle
 *   fs_drawer_nav   — drawer link clicks (area: pill|feature|category|quiet)
 *   fs_search_click — predictive search (area: predictive_result|predictive_chip)
 *   fs_chip_click   — collection project chips
 *   fs_hero_click   — homepage hero (area: cta|media)
 *   fs_rail_click   — PDP recipe rail cards
 *   fs_gifts_click  — gifts hub (area: card|band_all)
 *   fs_video_play   — videos page card plays (label = video id)
 *   fs_sticky_atc   — sticky ATC chip button (the add_to_cart ecommerce
 *                     event itself flows through Elevar via <product-form>;
 *                     this event only attributes WHICH button was used)
 */

(function () {
  'use strict';

  function push(name, params) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(Object.assign({ event: name }, params));
  }

  function label(el) {
    var t = (el.textContent || '').trim().replace(/\s+/g, ' ');
    return (t || el.getAttribute('href') || '').slice(0, 100);
  }

  // Order matters where surfaces nest (hero CTA sits over the media link).
  var RULES = [
    ['.fs-drawer__pill', 'fs_drawer_nav', { fs_area: 'pill' }],
    ['.fs-drawer__feature', 'fs_drawer_nav', { fs_area: 'feature' }],
    ['.fs-drawer__cat', 'fs_drawer_nav', { fs_area: 'category' }],
    ['.fs-drawer__link', 'fs_drawer_nav', { fs_area: 'quiet' }],
    ['.fs-ps__link', 'fs_search_click', { fs_area: 'predictive_result' }],
    ['.fs-ps__chip', 'fs_search_click', { fs_area: 'predictive_chip' }],
    ['.fs-tag-pills__pill', 'fs_chip_click', {}],
    ['.fs-hero__cta', 'fs_hero_click', { fs_area: 'cta' }],
    ['.fs-hero__media-link', 'fs_hero_click', { fs_area: 'media' }],
    // :not([data-fs-card]) — PLP cards share the fs-rail-card class family
    // but are tracked as dl_select_item below, not as rail clicks.
    ['.fs-rail-card:not([data-fs-card])', 'fs_rail_click', {}],
    ['.fs-gift-card', 'fs_gifts_click', { fs_area: 'card' }],
    ['.fs-gifts-hub__band-all', 'fs_gifts_click', { fs_area: 'band_all' }],
    ['.fs-vid__card', 'fs_video_play', {}],
    ['.fs-sticky-atc__button', 'fs_sticky_atc', {}],
  ];

  document.addEventListener(
    'click',
    function (e) {
      if (!e.target || !e.target.closest) return;
      for (var i = 0; i < RULES.length; i++) {
        var el = e.target.closest(RULES[i][0]);
        if (!el) continue;
        var params = Object.assign({}, RULES[i][2]);
        params.fs_label =
          el.getAttribute('data-video-id') || label(el);
        push(RULES[i][1], params);
        return;
      }
    },
    { capture: true, passive: true }
  );

  function initDrawer() {
    var drawer = document.getElementById('Details-menu-drawer-container');
    if (!drawer) return;
    drawer.addEventListener('toggle', function () {
      push(drawer.open ? 'fs_drawer_open' : 'fs_drawer_close', {});
    });
  }

  /* ---- Elevar-shape ecommerce rebuilds ----------------------------------
   * The redesign broke Elevar's own collection/cart listeners, so these
   * pushes recreate the exact dataLayer shapes the existing GTM tags read:
   *   dl_view_item_list -> ecommerce.impressions
   *   dl_select_item    -> ecommerce.click.products
   *   dl_remove_from_cart -> ecommerce.remove.products
   * Card data comes from data-fs-* attrs on .card-wrapper
   * (snippets/card-product.liquid). Keep shapes stable or update the GTM
   * variables (list_items / selected_product / removed_product) in step.
   */
  function cardData(el) {
    return {
      id: el.getAttribute('data-fs-pid') || '',
      name: el.getAttribute('data-fs-title') || '',
      price: el.getAttribute('data-fs-price') || '',
      handle: el.getAttribute('data-fs-handle') || '',
    };
  }

  function initViewItemList() {
    if (location.pathname.indexOf('/collections/') !== 0) return;
    var cards = [].slice.call(
      document.querySelectorAll('[data-fs-card][data-fs-pid]')
    );
    if (!cards.length) return;
    push('dl_view_item_list', {
      ecommerce: {
        currencyCode: 'USD',
        impressions: cards.map(function (el, i) {
          var d = cardData(el);
          return {
            id: d.id, name: d.name, price: d.price,
            position: i + 1, list: location.pathname,
          };
        }),
      },
    });
  }

  document.addEventListener(
    'click',
    function (e) {
      if (!e.target || !e.target.closest) return;

      var card = e.target.closest('[data-fs-card][data-fs-pid]');
      if (card && e.target.closest('a')) {
        var d = cardData(card);
        push('dl_select_item', {
          ecommerce: {
            currencyCode: 'USD',
            click: { products: [{ id: d.id, name: d.name, price: d.price }] },
          },
        });
        return;
      }

      var removeBtn = e.target.closest('.rebuy-cart__flyout-item-remove');
      if (removeBtn) {
        var item = removeBtn.closest('.rebuy-cart__flyout-item');
        var link = item && item.querySelector('a[href*="/products/"]');
        var handle = link
          ? (link.getAttribute('href').split('/products/')[1] || '').split('?')[0]
          : '';
        // /cart.js still holds the line at click time; removal is async after.
        fetch('/cart.js')
          .then(function (r) { return r.json(); })
          .then(function (cart) {
            var li = null;
            for (var i = 0; i < cart.items.length; i++) {
              if (cart.items[i].handle === handle) li = cart.items[i];
            }
            push('dl_remove_from_cart', {
              ecommerce: {
                currencyCode: 'USD',
                remove: {
                  products: [{
                    id: li ? String(li.product_id) : handle,
                    name: li ? li.product_title : handle,
                    price: li ? (li.price / 100).toFixed(2) : '',
                    quantity: li ? String(li.quantity) : '1',
                  }],
                },
              },
            });
          })
          .catch(function () {});
      }
    },
    { capture: true, passive: true }
  );

  function init() {
    initDrawer();
    initViewItemList();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
